/**
 * BottomSheet — reusable slide-up overlay
 *
 * V10: Fix backdrop flash + gesture reliability.
 *
 * V9 bugs:
 *   - Backdrop re-animated on every isDragging toggle (flash)
 *   - touchAction: none on entire sheet fought scroll container
 *
 * Fixes:
 *   - Track backdropReady ref — only animate once on entrance
 *   - Direct DOM opacity for backdrop, never re-trigger CSS animation after entrance
 *   - touchAction: none only on drag handle, pan-y on scroll container
 *   - Consolidated gesture state to avoid stale closures
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { T } from '@/styles/tokens';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  variant?: 'default' | 'verdict';
  /** Custom top offset in px (distance from top of viewport to sheet top). Overrides default HEADER_OFFSET. */
  headerOffset?: number;
}

const HEADER_OFFSET = 52;
const VELOCITY_DISMISS = 600;
const DISTANCE_DISMISS = 180;
const DISMISS_DURATION_BASE = 300;
const DISMISS_DURATION_MAX = 450;
const VERDICT_DRAG_ZONE = 64;
const INTENT_THRESHOLD = 8;

// Spring snap-back params
const SPRING_TENSION = 300;
const SPRING_DAMPING = 24;

export function BottomSheet({ isOpen, onClose, children, variant = 'default', headerOffset }: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const isVerdict = variant === 'verdict';

  const [visible, setVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const entranceDone = useRef(false);
  const dismissingRef = useRef(false);
  const backdropReady = useRef(false); // true once entrance backdrop fade completes

  // Gesture tracking
  const dragStartY = useRef(0);
  const dragStartTime = useRef(0);
  const tracking = useRef(false);
  const intentDecided = useRef(false);
  const isDragIntent = useRef(false);

  // RAF-based drag state (bypass React setState for smooth 60fps)
  const currentDragY = useRef(0);
  const rafId = useRef(0);
  const springRaf = useRef(0);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // ── Direct DOM manipulation for drag (no React re-render) ───────

  const applyDragTransform = useCallback((dy: number) => {
    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    if (sheet) {
      sheet.style.transform = dy !== 0 ? `translateY(${Math.max(0, dy)}px)` : '';
      sheet.style.transition = 'none';
    }
    if (backdrop) {
      const opacity = dy > 0 ? Math.max(0, 1 - dy / 500) : 1;
      backdrop.style.opacity = String(opacity);
      backdrop.style.transition = 'none';
    }
    currentDragY.current = dy;
  }, []);

  // Logarithmic rubber band — feels softer at extremes
  const rubberBand = useCallback((dy: number): number => {
    if (dy >= 0) return dy;
    const abs = Math.abs(dy);
    return -(Math.log(abs / 20 + 1) * 20);
  }, []);

  // ── Spring snap-back (animated return to 0) ─────────────────────

  const springSnapBack = useCallback(() => {
    cancelAnimationFrame(springRaf.current);
    let velocity = 0;
    let position = currentDragY.current;
    const dt = 1 / 60;

    const tick = () => {
      const springForce = -SPRING_TENSION * position;
      const dampingForce = -SPRING_DAMPING * velocity;
      const acceleration = springForce + dampingForce;
      velocity += acceleration * dt;
      position += velocity * dt;

      if (Math.abs(position) < 0.5 && Math.abs(velocity) < 0.5) {
        // Settled — restore to clean state without re-triggering animations
        currentDragY.current = 0;
        const sheet = sheetRef.current;
        if (sheet) {
          sheet.style.transform = '';
          sheet.style.transition = '';
        }
        const backdrop = backdropRef.current;
        if (backdrop) {
          backdrop.style.opacity = '1';
          backdrop.style.transition = '';
        }
        setIsDragging(false);
        return;
      }

      applyDragTransform(position);
      springRaf.current = requestAnimationFrame(tick);
    };

    springRaf.current = requestAnimationFrame(tick);
  }, [applyDragTransform]);

  // ── Momentum dismiss (velocity-aware slide off) ─────────────────

  const momentumDismiss = useCallback((gestureVelocity: number) => {
    dismissingRef.current = true;
    setIsDragging(false);

    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    const speed = Math.abs(gestureVelocity);
    const duration = Math.max(
      180,
      Math.min(DISMISS_DURATION_MAX, DISMISS_DURATION_BASE - (speed / 3000) * 150)
    );

    if (sheet) {
      sheet.style.transition = `transform ${duration}ms cubic-bezier(0.36, 0, 0.66, -0.05)`;
      sheet.style.transform = `translateY(${window.innerHeight}px)`;
    }
    if (backdrop) {
      backdrop.style.transition = `opacity ${duration}ms ease-out`;
      backdrop.style.opacity = '0';
    }

    setTimeout(() => {
      setVisible(false);
      currentDragY.current = 0;
      backdropReady.current = false;
      if (sheet) {
        sheet.style.transform = '';
        sheet.style.transition = '';
      }
      if (backdrop) {
        backdrop.style.opacity = '';
        backdrop.style.transition = '';
        backdrop.style.animation = '';
      }
      dismissingRef.current = false;
      onCloseRef.current();
    }, duration + 20);
  }, []);

  // Mount when isOpen becomes true
  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      currentDragY.current = 0;
      entranceDone.current = false;
      dismissingRef.current = false;
      backdropReady.current = false;
    }
  }, [isOpen]);

  // Lock body scroll + block pull-to-refresh
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.overscrollBehavior = 'none';
      document.documentElement.style.overscrollBehavior = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.overscrollBehavior = '';
      document.documentElement.style.overscrollBehavior = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.overscrollBehavior = '';
      document.documentElement.style.overscrollBehavior = '';
    };
  }, [isOpen]);

  // Cleanup RAFs on unmount
  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafId.current);
      cancelAnimationFrame(springRaf.current);
    };
  }, []);

  // Handle animation end — controls unmounting + backdrop state
  const handleAnimationEnd = useCallback((e: React.AnimationEvent) => {
    if (e.animationName === 'bsSlideUp') {
      entranceDone.current = true;
    }
    if (e.animationName === 'bsSlideDown') {
      setVisible(false);
      dismissingRef.current = false;
      backdropReady.current = false;
    }
  }, []);

  const handleBackdropAnimEnd = useCallback((e: React.AnimationEvent) => {
    if (e.animationName === 'bsBackdropIn') {
      backdropReady.current = true;
      // Lock opacity at 1 so no further CSS animation can flash it
      const backdrop = backdropRef.current;
      if (backdrop) {
        backdrop.style.opacity = '1';
      }
    }
  }, []);

  // ── Native touch listeners ────────────────────────────────────────

  useEffect(() => {
    if (!isOpen || !visible) return;
    const sheet = sheetRef.current;
    if (!sheet) return;

    function onTouchStart(e: TouchEvent) {
      if (!e.touches.length) return;

      // Don't intercept touches on interactive elements (buttons, links, inputs)
      const target = e.target as HTMLElement;
      if (target.closest('button, a, input, select, textarea, [role="button"]')) return;

      cancelAnimationFrame(springRaf.current);
      const touch = e.touches[0];
      const rect = sheet!.getBoundingClientRect();
      const yInSheet = touch.clientY - rect.top;

      intentDecided.current = false;
      isDragIntent.current = false;
      tracking.current = true;
      dragStartY.current = touch.clientY;
      dragStartTime.current = Date.now();

      if (yInSheet <= VERDICT_DRAG_ZONE) {
        e.preventDefault();
        intentDecided.current = true;
        isDragIntent.current = true;
        setIsDragging(true);
        applyDragTransform(0);
      }
    }

    function onTouchMove(e: TouchEvent) {
      if (!tracking.current || !e.touches.length) return;
      const touch = e.touches[0];
      const rawDy = touch.clientY - dragStartY.current;

      if (!intentDecided.current) {
        if (Math.abs(rawDy) < INTENT_THRESHOLD) return;
        const scrollEl = scrollRef.current;
        const isAtScrollTop = !scrollEl || scrollEl.scrollTop <= 1;
        if (isAtScrollTop && rawDy > 0) {
          e.preventDefault();
          isDragIntent.current = true;
          intentDecided.current = true;
          setIsDragging(true);
          dragStartY.current = touch.clientY;
          applyDragTransform(0);
        } else {
          isDragIntent.current = false;
          intentDecided.current = true;
          tracking.current = false;
        }
        return;
      }

      if (!isDragIntent.current) return;
      e.preventDefault();
      const dy = touch.clientY - dragStartY.current;
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        applyDragTransform(dy > 0 ? dy : rubberBand(dy));
      });
    }

    function onTouchEnd(e: TouchEvent) {
      if (!tracking.current) return;
      cancelAnimationFrame(rafId.current);
      if (!isDragIntent.current) {
        tracking.current = false;
        intentDecided.current = false;
        return;
      }

      const touch = e.changedTouches[0];
      if (!touch) { tracking.current = false; return; }

      const elapsed = Math.max(1, Date.now() - dragStartTime.current);
      const totalDy = touch.clientY - dragStartY.current;
      const velocity = (totalDy / elapsed) * 1000;
      const shouldDismiss = velocity > VELOCITY_DISMISS || totalDy > DISTANCE_DISMISS;

      tracking.current = false;
      intentDecided.current = false;
      isDragIntent.current = false;

      if (shouldDismiss) {
        momentumDismiss(velocity);
      } else {
        springSnapBack();
      }
    }

    function onTouchCancel() {
      tracking.current = false;
      intentDecided.current = false;
      isDragIntent.current = false;
      cancelAnimationFrame(rafId.current);
      springSnapBack();
    }

    sheet.addEventListener('touchstart', onTouchStart, { passive: false });
    sheet.addEventListener('touchmove', onTouchMove, { passive: false });
    sheet.addEventListener('touchend', onTouchEnd, { passive: true });
    sheet.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return () => {
      sheet.removeEventListener('touchstart', onTouchStart);
      sheet.removeEventListener('touchmove', onTouchMove);
      sheet.removeEventListener('touchend', onTouchEnd);
      sheet.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [isOpen, visible, applyDragTransform, rubberBand, springSnapBack, momentumDismiss]);

  // ── Mouse drag fallback (desktop) ─────────────────────────────────

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'touch') return;

    // Don't intercept clicks on interactive elements (buttons, links, inputs)
    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, select, textarea, [role="button"]')) return;

    cancelAnimationFrame(springRaf.current);
    const rect = sheetRef.current?.getBoundingClientRect();
    if (!rect) return;
    const yInSheet = e.clientY - rect.top;

    intentDecided.current = false;
    isDragIntent.current = false;
    tracking.current = true;
    dragStartY.current = e.clientY;
    dragStartTime.current = Date.now();

    if (yInSheet <= VERDICT_DRAG_ZONE) {
      e.preventDefault();
      try { (e.target as HTMLElement).setPointerCapture(e.pointerId); } catch {}
      intentDecided.current = true;
      isDragIntent.current = true;
      setIsDragging(true);
      applyDragTransform(0);
    }
  }, [applyDragTransform]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'touch' || !tracking.current) return;
    const rawDy = e.clientY - dragStartY.current;

    if (!intentDecided.current) {
      if (Math.abs(rawDy) < INTENT_THRESHOLD) return;
      const scrollEl = scrollRef.current;
      const isAtScrollTop = !scrollEl || scrollEl.scrollTop <= 1;
      if (isAtScrollTop && rawDy > 0) {
        e.preventDefault();
        isDragIntent.current = true;
        intentDecided.current = true;
        try { (e.target as HTMLElement).setPointerCapture(e.pointerId); } catch {}
        setIsDragging(true);
        dragStartY.current = e.clientY;
        applyDragTransform(0);
      } else {
        isDragIntent.current = false;
        intentDecided.current = true;
        tracking.current = false;
      }
      return;
    }

    if (!isDragIntent.current) return;
    e.preventDefault();
    const dy = e.clientY - dragStartY.current;
    cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      applyDragTransform(dy > 0 ? dy : rubberBand(dy));
    });
  }, [applyDragTransform, rubberBand]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'touch' || !tracking.current) return;
    cancelAnimationFrame(rafId.current);
    if (!isDragIntent.current) { tracking.current = false; return; }

    const elapsed = Math.max(1, Date.now() - dragStartTime.current);
    const totalDy = e.clientY - dragStartY.current;
    const velocity = (totalDy / elapsed) * 1000;
    const shouldDismiss = velocity > VELOCITY_DISMISS || totalDy > DISTANCE_DISMISS;

    tracking.current = false;
    intentDecided.current = false;
    isDragIntent.current = false;

    if (shouldDismiss) {
      momentumDismiss(velocity);
    } else {
      springSnapBack();
    }
  }, [momentumDismiss, springSnapBack]);

  const handlePointerCancel = useCallback(() => {
    tracking.current = false;
    intentDecided.current = false;
    isDragIntent.current = false;
    cancelAnimationFrame(rafId.current);
    springSnapBack();
  }, [springSnapBack]);

  // ── Render gate ───────────────────────────────────────────────────
  if (!visible) return null;

  // ── Computed styles ────────────────────────────────────────────────

  const offset = headerOffset ?? HEADER_OFFSET;
  // Use 100dvh (dynamic viewport height) so iOS Safari doesn't overshoot past the visible area.
  // Fallback to 100vh for older browsers — the sheet still works, just slightly taller.
  const sheetHeight = `calc(100dvh - ${offset}px - env(safe-area-inset-top, 0px))`;

  // Entrance: only on first open, not after snap-back or during dismiss
  const shouldPlayEntrance = isOpen && !entranceDone.current && !dismissingRef.current;
  // Exit: only for non-drag close (backdrop tap, programmatic)
  const shouldPlayExit = !isOpen && !dismissingRef.current;

  let sheetAnimation: string | undefined;
  if (shouldPlayEntrance) {
    sheetAnimation = `bsSlideUp 380ms cubic-bezier(0.16, 1.11, 0.3, 1.0) forwards`;
  } else if (shouldPlayExit) {
    sheetAnimation = 'bsSlideDown 280ms cubic-bezier(0.36, 0, 0.66, -0.05) forwards';
  }

  // Backdrop animation: ONLY on entrance, never re-triggered
  let backdropAnimation: string | undefined;
  if (shouldPlayEntrance && !backdropReady.current) {
    backdropAnimation = 'bsBackdropIn 250ms ease-out forwards';
  } else if (shouldPlayExit) {
    backdropAnimation = 'bsBackdropOut 280ms ease-in forwards';
  }

  return (
    <>
      <style>{`
        @keyframes bsBackdropIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes bsBackdropOut { from { opacity: 1; } to { opacity: 0; } }
        @keyframes bsSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes bsSlideDown { from { transform: translateY(0); } to { transform: translateY(100%); } }
      `}</style>

      {/* Backdrop — touchAction:none prevents ANY touch from reaching the page below */}
      <div
        ref={backdropRef}
        onAnimationEnd={handleBackdropAnimEnd}
        onClick={onClose}
        onTouchMove={(e) => e.preventDefault()}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(26,26,26,0.4)',
          zIndex: 300,
          animation: backdropAnimation,
          pointerEvents: isOpen ? 'auto' : 'none',
          cursor: 'pointer',
          touchAction: 'none',
          // After entrance, keep at full opacity — drag code mutates directly
          ...(backdropReady.current && !shouldPlayExit ? { opacity: 1 } : {}),
        }}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        onAnimationEnd={handleAnimationEnd}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          ...(isVerdict
            ? { height: sheetHeight }
            : { maxHeight: sheetHeight }),
          backgroundColor: isVerdict ? T.cream : T.white,
          borderRadius: '20px 20px 0 0',
          boxShadow: '0 -4px 32px rgba(26,26,26,0.14)',
          zIndex: 301,
          display: 'flex',
          flexDirection: 'column',
          animation: sheetAnimation,
          willChange: 'transform',
          userSelect: isDragging ? 'none' : undefined,
          WebkitUserSelect: isDragging ? 'none' : undefined,
          overscrollBehavior: 'none',
        }}
      >
        {/* Drag handle zone — touchAction:none so browser never steals these touches */}
        <div
          style={{
            position: 'relative',
            paddingTop: 12,
            paddingBottom: 8,
            flexShrink: 0,
            cursor: 'grab',
            touchAction: 'none',
          }}
        >
          {/* Handle pill */}
          <div
            style={{
              width: 36,
              height: 4,
              borderRadius: 2,
              backgroundColor: T.textMuted,
              margin: '0 auto',
            }}
          />
        </div>

        {/* Scrollable content — pan-y allows vertical scroll, JS handles drag takeover */}
        <div
          ref={scrollRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: isVerdict ? 0 : 0,
            pointerEvents: isDragging ? 'none' : undefined,
            overscrollBehavior: 'contain',
            touchAction: 'pan-y',
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
}
